/**
 * Blog service for Shopify operations
 * Handles all blog and article-related API calls
 */

import { BaseShopifyService } from "../services/base";
import {
  mapGetArticleToResponse,
  mapGetBlogArticlesToResponse,
  mapGetTrendingArticlesToResponse,
  mapGetArticleByHandleToResponse,
} from "./mappers";
import {
  GET_BLOG_ARTICLES,
  GET_ARTICLE_BY_HANDLE,
  GET_TRENDING_ARTICLES,
  GET_ARTICLES_QUERY,
} from "./queries";
import {
  IOptionsRequest,
  IResponse,
  IBlogArticleRequest,
  ITrendingArticleRequest,
  IArticleByHandleRequest,
  IGetArticleRequest,
} from "../../../models";
import { formatResponse } from "../../../core/utils";

export class BlogService extends BaseShopifyService {
  /**
   * Get all articles from all blogs with optional parameters
   * Supports custom query and variables through options
   */
  async getArticle(
    params?: IGetArticleRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      // Use custom query from options if provided, otherwise use default
      const query = options?.query || GET_ARTICLES_QUERY;

      // Merge params with custom variables from options
      const variables = {
        blogLimit: params?.blogLimit,
        articleLimit: params?.articleLimit,
        query: params?.query,
        sortKey: params?.sortKey,
        reverse: params?.reverse,
        ...options?.variables, // Allow custom variables to override defaults
      };

      const { result, errors } = await this.query(query, variables, options);

      // ✅ Handle GraphQL or Shopify userErrors
      if (errors && errors.length > 0) {
        this.logger.error("GraphQL errors in GET_ARTICLES", undefined, {
          errors,
        });

        return formatResponse({
          success: false,
          message: "Failed to fetch articles due to upstream errors.",
          error: {
            message: "GraphQL errors received.",
            details: errors,
          },
        });
      }

      // ✅ Handle missing / empty data
      if (!result?.data || !result.data.blogs) {
        return formatResponse({
          success: true,
          message: "Request completed successfully. No blogs found.",
          data: [],
          meta: undefined,
        });
      }

      // ✅ Normal success path
      const articles = mapGetArticleToResponse(result.data.blogs);

      return formatResponse({
        success: true,
        message: "Articles retrieved successfully.",
        data: articles,
        meta: undefined,
      });
    } catch (error: any) {
      // ✅ Log the error safely
      this.logger.error("Error in getArticle", undefined, { error });

      const safeMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing the request.";

      return formatResponse({
        success: false,
        message: "An error occurred while processing the request.",
        error: {
          message: safeMessage,
          details: error,
        },
      });
    }
  }

  /**
   * Get blog articles with enhanced parameter support
   */
  async getBlogArticles(
    params: IBlogArticleRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      const {
        category,
        limit = 10,
        after,
        before,
        query,
        sortKey,
        reverse,
      } = params;

      if (!category) {
        // If no category specified, return all articles
        return this.getArticle(undefined, options);
      }

      // Use custom query from options if provided, otherwise use default
      const queryToUse = options?.query || GET_BLOG_ARTICLES;

      // Merge params with custom variables from options
      const variables = {
        blogHandle: category,
        limit,
        after,
        before,
        query,
        sortKey,
        reverse,
        ...options?.variables, // Allow custom variables to override defaults
      };

      const { result, errors } = await this.query(
        queryToUse,
        variables,
        options
      );

      // ✅ Handle GraphQL or Shopify userErrors
      if (errors && errors.length > 0) {
        this.logger.error("GraphQL errors in GET_BLOG_ARTICLES", undefined, {
          errors,
        });

        return formatResponse({
          success: false,
          message: "Failed to fetch blog articles due to upstream errors.",
          error: {
            message: "GraphQL errors received.",
            details: errors,
          },
        });
      }

      // ✅ Handle missing / empty data
      if (!result?.data || !result.data.blog) {
        return formatResponse({
          success: true,
          message: "Request completed successfully. Blog not found.",
          data: null,
          meta: undefined,
        });
      }

      // ✅ Normal success path
      const responseData = mapGetBlogArticlesToResponse(result.data.blog);

      return formatResponse({
        success: true,
        message: "Blog articles retrieved successfully.",
        data: responseData,
        meta: {
          pagination: result.data.blog.articles?.pageInfo,
        },
      });
    } catch (error: any) {
      // ✅ Log the error safely
      this.logger.error("Error in getBlogArticles", undefined, { error });

      const safeMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing the request.";

      return formatResponse({
        success: false,
        message: "An error occurred while processing the request.",
        error: {
          message: safeMessage,
          details: error,
        },
      });
    }
  }

  /**
   * Get trending articles with enhanced parameter support
   * Supports custom query and variables through options
   */
  async getTrendingArticles(
    params: ITrendingArticleRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      if (!params.category) {
        return formatResponse({
          success: true,
          message: "No category specified",
          data: [],
        });
      }

      // Use custom query from options if provided, otherwise use default
      const query = options?.query || GET_TRENDING_ARTICLES;

      // Merge params with custom variables from options
      const variables = {
        handle: params.category,
        limit: params.limit,
        query: params.query,
        ...options?.variables, // Allow custom variables to override defaults
      };

      const { result, errors } = await this.query(query, variables, options);

      // ✅ Handle GraphQL or Shopify userErrors
      if (errors && errors.length > 0) {
        this.logger.error(
          "GraphQL errors in GET_TRENDING_ARTICLES",
          undefined,
          {
            errors,
          }
        );

        return formatResponse({
          success: false,
          message: "Failed to fetch trending articles due to upstream errors.",
          error: {
            message: "GraphQL errors received.",
            details: errors,
          },
        });
      }

      // ✅ Handle missing / empty data
      if (!result?.data || !result.data.blog) {
        return formatResponse({
          success: true,
          message:
            "Request completed successfully. No trending articles found.",
          data: [],
          meta: undefined,
        });
      }

      // ✅ Normal success path
      const articles = mapGetTrendingArticlesToResponse(result.data.blog);

      return formatResponse({
        success: true,
        message: "Trending articles retrieved successfully.",
        data: articles,
        meta: undefined,
      });
    } catch (error: any) {
      // ✅ Log the error safely
      this.logger.error("Error in getTrendingArticles", undefined, { error });

      const safeMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing the request.";

      return formatResponse({
        success: false,
        message: "An error occurred while processing the request.",
        error: {
          message: safeMessage,
          details: error,
        },
      });
    }
  }

  /**
   * Get a single article by handle
   * Supports custom query and variables through options
   */
  async getArticleByHandle(
    params: IArticleByHandleRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      // Use custom query from options if provided, otherwise use default
      const query = options?.query || GET_ARTICLE_BY_HANDLE;

      // Merge params with custom variables from options
      const variables = {
        blogHandle: params.category,
        articleHandle: params.articleHandle,
        ...options?.variables, // Allow custom variables to override defaults
      };

      const { result, errors } = await this.query(query, variables, options);

      // ✅ Handle GraphQL or Shopify userErrors
      if (errors && errors.length > 0) {
        this.logger.error(
          "GraphQL errors in GET_ARTICLE_BY_HANDLE",
          undefined,
          {
            errors,
          }
        );

        return formatResponse({
          success: false,
          message: "Failed to fetch article due to upstream errors.",
          error: {
            message: "GraphQL errors received.",
            details: errors,
          },
        });
      }

      // ✅ Handle missing / empty data
      if (
        !result?.data ||
        !result.data.blog ||
        !result.data.blog.articleByHandle
      ) {
        return formatResponse({
          success: true,
          message: "Request completed successfully. Article not found.",
          data: null,
          meta: undefined,
        });
      }

      // ✅ Normal success path
      const article = mapGetArticleByHandleToResponse(
        result.data.blog.articleByHandle
      );

      return formatResponse({
        success: true,
        message: "Article retrieved successfully.",
        data: article,
        meta: undefined,
      });
    } catch (error: any) {
      // ✅ Log the error safely
      this.logger.error("Error in getArticleByHandle", undefined, { error });

      const safeMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing the request.";

      return formatResponse({
        success: false,
        message: "An error occurred while processing the request.",
        error: {
          message: safeMessage,
          details: error,
        },
      });
    }
  }
}
